import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lock, Mail, ArrowLeft, ShieldAlert } from "lucide-react";
import api from "@/lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Något gick fel. Försök igen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [blockStatus, setBlockStatus] = useState(null); // null | {blocked, ip, attempts}

  useEffect(() => {
    // Public block check — hides the form entirely if this IP is blocked.
    api.get("/auth/check-block")
      .then(({ data }) => setBlockStatus(data))
      .catch(() => setBlockStatus({ blocked: false }));
  }, []);

  if (loading) return <div className="min-h-screen grid place-items-center text-[var(--text-muted)]">Laddar…</div>;
  if (user && user !== false) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(detail);
      // If we just got blocked, hide the form
      if (err.response?.status === 403 && /blockerad/i.test(detail)) {
        const { data } = await api.get("/auth/check-block").catch(() => ({ data: null }));
        if (data) setBlockStatus(data);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isBlocked = blockStatus?.blocked === true;

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-black text-white">
      <div className="hidden md:flex relative bg-black border-r border-white/10 flex-col justify-between p-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white">
          <ArrowLeft size={16} /> Tillbaka till webbplatsen
        </Link>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-white/60 font-semibold mb-3">
            Restaurang Mon Ami · Admin
          </div>
          <h1 className="font-serif-display text-5xl font-black leading-[1.05] text-white">
            Hantera meny <br />
            <span className="italic font-medium text-white/80">enkelt &amp; snabbt</span>
          </h1>
          <div className="mt-8 h-px w-24 bg-white/20" />
        </div>
        <div className="text-xs text-white/40 tracking-wider">© {new Date().getFullYear()} · Endast personal</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-black">
        {isBlocked ? (
          <div className="w-full max-w-md space-y-6 text-center" data-testid="login-blocked">
            <div className="md:hidden mb-2 text-left">
              <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-white/60">
                <ArrowLeft size={16} /> Tillbaka
              </Link>
            </div>
            <div className="w-16 h-16 mx-auto rounded-full bg-red-500/15 border border-red-500/30 grid place-items-center">
              <ShieldAlert size={32} className="text-red-300" />
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-black text-white">Åtkomst nekad</h2>
            <p className="text-white/60">
              Din IP-adress (<code className="bg-white/10 text-white px-2 py-0.5 rounded text-sm">{blockStatus.ip}</code>) är blockerad från adminpanelen efter <strong className="text-white">{blockStatus.attempts || 5}+ misslyckade inloggningsförsök</strong>.
            </p>
            <p className="text-sm text-white/50">
              Kontakta administratören för att häva blockeringen. Denna spärr försvinner inte automatiskt.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:underline">
              <ArrowLeft size={14} /> Till webbplatsen
            </Link>
          </div>
        ) : (
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div>
            <div className="md:hidden mb-4">
              <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-white/60">
                <ArrowLeft size={16} /> Tillbaka
              </Link>
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-black text-white">Logga in</h2>
            <p className="text-white/50 mt-2">Endast för personal på Mon Ami.</p>
          </div>

          <div className="space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-white/60">E-post</span>
              <div className="mt-1 relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="email"
                  required
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white/5 text-white placeholder:text-white/30 border border-white/15 rounded-xl pl-11 pr-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/60"
                  placeholder="admin@monami.se"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-white/60">Lösenord</span>
              <div className="mt-1 relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="password"
                  required
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/5 text-white placeholder:text-white/30 border border-white/15 rounded-xl pl-11 pr-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/60"
                  placeholder="••••••••"
                />
              </div>
            </label>
          </div>

          {error && (
            <div data-testid="login-error" className="text-sm text-red-200 bg-red-900/40 border border-red-500/40 rounded-xl p-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit-button"
            className="w-full rounded-full bg-white hover:bg-white/90 text-black py-4 font-bold disabled:opacity-40 transition-colors"
          >
            {submitting ? "Loggar in…" : "Logga in"}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
